// ADR 6.9.26c §1 — the consent gate, and nothing else.
//
// Pure on purpose: no SDK import, no `fetch`, no DOM beyond `localStorage`.
// ADR 30.7.26a found that the opt-out has to be read *before* either SDK is
// constructed — a post-`init` `optOut()` call arrives after the SDK's own
// automatic lifecycle events have already fired, which is exactly the leak the
// toggle exists to prevent — and that "device-local flags are the gating source
// of truth … gating must not depend on the network or auth". Both follow from
// keeping the read synchronous and keeping it here.
//
// So the account row (`GET /settings/privacy`) is a *reconcile* input, merged
// most-restrictive-wins after the first render, never something construction
// waits on. `index.ts` owns that request; this module owns the rule.

/**
 * The account-wide pair, spelled as the API spells it
 * (`rust-core/src/client/client_privacy.rs` — `PrivacySettings`), so the
 * reconcile is a field copy rather than a translation table.
 */
export interface TelemetryConsent {
  analytics_opt_out: boolean
  crash_reports_opt_out: boolean
  /**
   * ADR 30.9.26l §1 — present only when a person answered: the consent strip,
   * or an `/account` toggle. It is what tells "never asked" from "declined",
   * which the pair alone cannot: the reconcile writes `{true,true}` for every
   * signed-in session. Never on the server row, and never read by the gate.
   */
  answered?: true
}

/**
 * A stranger is measured by nothing at all.
 *
 * This **diverges from the native clients deliberately**: 30.7.26a defaults
 * both flags to `false` (collection on; Play requires an opt-*out*). On mobile
 * the account is reachable within a launch or two, and on the web most sessions
 * never sign in — the pre-sign-in surface is the indexable, shareable one, so
 * the anonymous case is the common case rather than the edge. ADR 5.9.26p is
 * where this is revisited with an actual sign-in flow to hang the question off.
 */
export const DEFAULT_CONSENT: TelemetryConsent = {
  analytics_opt_out: true,
  crash_reports_opt_out: true,
}

/** `lingara.web.<thing>.v1`, the key shape `auth/tokenStore.ts` already uses. */
export const CONSENT_KEY = 'lingara.web.telemetry.v1'

/**
 * Is this the pair, with both flags actually boolean?
 *
 * Exported because the reconcile needs it too: `GET /settings/privacy` is the
 * one input to this module that is neither a literal nor something this browser
 * wrote, and `local || server` over an `undefined` field would persist
 * `undefined` as a consent record — which the next `readLocalConsent()` would
 * then reject as corrupt and answer with the denied default. Failing safe twice
 * is still a bug worth not having.
 */
export function isTelemetryConsent(value: unknown): value is TelemetryConsent {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  return (
    typeof record.analytics_opt_out === 'boolean' &&
    typeof record.crash_reports_opt_out === 'boolean'
  )
}

/**
 * The gate, read synchronously from `localStorage`.
 *
 * Every failure resolves to {@link DEFAULT_CONSENT}: absent, unparseable, the
 * wrong shape, or a browser that throws on `localStorage` access (Safari in a
 * blocked-storage frame does). Failing to the denied default is the only safe
 * direction — the alternative is constructing an SDK because a JSON parse
 * failed.
 */
export function readLocalConsent(): TelemetryConsent {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY)
    if (raw === null) return DEFAULT_CONSENT
    const parsed: unknown = JSON.parse(raw)
    if (!isTelemetryConsent(parsed)) return DEFAULT_CONSENT
    // The pair, and only the pair: the marker is `hasAnsweredConsent`'s.
    return { analytics_opt_out: parsed.analytics_opt_out, crash_reports_opt_out: parsed.crash_reports_opt_out }
  } catch {
    return DEFAULT_CONSENT
  }
}

/** The stored record's marker, strictly `true`; `null` when storage throws. */
function storedAnswered(): boolean | null {
  let raw: string | null
  try {
    raw = window.localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
  if (raw === null) return false
  try {
    const parsed: unknown = JSON.parse(raw)
    return isTelemetryConsent(parsed) && parsed.answered === true
  } catch {
    return false
  }
}

/**
 * ADR 30.9.26l §1 — has a person answered the consent question in this browser?
 *
 * `true` also when storage throws: a browser that cannot keep the answer would
 * otherwise be asked on every page, and could never make the question stop. A
 * corrupt record is `false`, so the visitor is asked again — the only way out
 * of a record {@link readLocalConsent} already ignores.
 */
export function hasAnsweredConsent(): boolean {
  return storedAnswered() ?? true
}

/**
 * `consent` carrying the stored record's marker, if it has one — never adding
 * one it lacks. The reconcile's write goes through here: it is not a person's
 * act, so it may preserve an answer but never invent one.
 */
export function keepAnswered(consent: TelemetryConsent): TelemetryConsent {
  const pair = { analytics_opt_out: consent.analytics_opt_out, crash_reports_opt_out: consent.crash_reports_opt_out }
  return storedAnswered() === true ? { ...pair, answered: true } : pair
}

/** Persist a record. A storage failure is not worth failing a render over. */
export function writeLocalConsent(consent: TelemetryConsent): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(consent))
  } catch {
    // Nothing to do and nothing to report: the next read falls back to the
    // denied default, which is the conservative side of the failure.
  }
}

/**
 * Most-restrictive-wins, exactly as on the native clients: `merged = local OR
 * server`.
 *
 * The account row can therefore only ever *remove* consent relative to the
 * local flag, so signing in never silently reverses an opt-out and the
 * reconcile needs no UI to be correct.
 */
export function mergeConsent(
  local: TelemetryConsent,
  server: TelemetryConsent,
): TelemetryConsent {
  return {
    analytics_opt_out: local.analytics_opt_out || server.analytics_opt_out,
    crash_reports_opt_out: local.crash_reports_opt_out || server.crash_reports_opt_out,
  }
}

/** True when neither SDK may be constructed — the whole of the strangers' path. */
export function deniesEverything(consent: TelemetryConsent): boolean {
  return consent.analytics_opt_out && consent.crash_reports_opt_out
}
